import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, Timestamp, setDoc, doc, query, limit } from 'firebase/firestore';
import { db } from './firebase';
import LandingPage from './LandingPage';
import './App.css';

function App() {
  const [landingPageData, setLandingPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [landingPageId, setLandingPageId] = useState(null);
  const [viewMode, setViewMode] = useState('desktop'); // Para responsividade

  // Detectar dispositivo móvel
  useEffect(() => {
    const checkDevice = () => {
      const isMobile = window.innerWidth <= 768;
      setViewMode(isMobile ? 'mobile' : 'desktop');
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Carregar a primeira landing page disponível
  useEffect(() => {
    const loadLandingPageData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log('Buscando landing pages disponíveis...');
        
        // Buscar todas as landing pages (ou limitar a 1)
        const q = query(collection(db, 'landingPages'), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Pegar a primeira landing page encontrada
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          const docId = doc.id;
          
          console.log('Landing page encontrada:', docId);
          console.log('Dados:', data);
          
          setLandingPageData(data);
          setLandingPageId(docId);
          
          // Registrar visita da página
          await recordPageVisit(data.id || docId);
          
          // Registrar usuário ativo
          await registerActiveUser(docId);
        } else {
          console.log('Nenhuma landing page encontrada');
          setError('Nenhuma landing page disponível');
        }
      } catch (error) {
        console.error('Erro ao carregar landing page:', error);
        setError('Erro ao carregar a página');
      } finally {
        setIsLoading(false);
      }
    };

    loadLandingPageData();
  }, []);

  // Registrar visita da página
  const recordPageVisit = async (landingPageId) => {
    try {
      await addDoc(collection(db, 'pageVisits'), {
        landingPageId: landingPageId,
        userId: landingPageId,
        timestamp: Timestamp.now(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        device: viewMode
      });
      console.log('Visita registrada com sucesso');
    } catch (error) {
      console.error('Erro ao registrar visita:', error);
    }
  };

  // Registrar usuário ativo
  const registerActiveUser = async (landingPageId) => {
    try {
      const sessionId = generateSessionId();
      const activeUserRef = doc(db, 'activeUsers', sessionId);
      
      await setDoc(activeUserRef, {
        userId: landingPageId,
        lastActive: Timestamp.now(),
        sessionId: sessionId,
        userAgent: navigator.userAgent,
        device: viewMode
      });

      // Atualizar a cada 30 segundos
      const interval = setInterval(async () => {
        try {
          await setDoc(activeUserRef, {
            userId: landingPageId,
            lastActive: Timestamp.now(),
            sessionId: sessionId,
            userAgent: navigator.userAgent,
            device: viewMode
          });
        } catch (error) {
          console.error('Erro ao atualizar usuário ativo:', error);
          clearInterval(interval);
        }
      }, 30000);

      // Limpar ao sair da página
      window.addEventListener('beforeunload', () => {
        clearInterval(interval);
      });

      return () => clearInterval(interval);
    } catch (error) {
      console.error('Erro ao registrar usuário ativo:', error);
    }
  };

  // Gerar ID de sessão único
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Registrar clique em botão - ATUALIZADO para múltiplos botões
  const recordButtonClick = async (buttonText, buttonLink, sectionType, buttonIndex = 0) => {
    try {
      await addDoc(collection(db, 'clickMetrics'), {
        button: buttonText || 'Botão sem texto',
        link: buttonLink || '#',
        sectionType: sectionType || 'unknown',
        buttonIndex: buttonIndex,
        landingPageId: landingPageData?.id || landingPageId,
        userId: landingPageId,
        timestamp: Timestamp.now(),
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        device: viewMode,
        sessionId: generateSessionId()
      });
      console.log('Clique registrado:', buttonText);
    } catch (error) {
      console.error('Erro ao registrar clique:', error);
    }
  };

  // Manipular cliques em links/botões - ATUALIZADO
  const handleButtonClick = (buttonText, buttonLink, sectionType, buttonIndex = 0) => {
    // Registrar o clique
    recordButtonClick(buttonText, buttonLink, sectionType, buttonIndex);

    // Se for um link externo, abrir em nova aba
    if (buttonLink && buttonLink.startsWith('http')) {
      window.open(buttonLink, '_blank', 'noopener,noreferrer');
    } else if (buttonLink && buttonLink.startsWith('#')) {
      // Se for âncora, fazer scroll suave
      const element = document.querySelector(buttonLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (buttonLink && buttonLink !== '#') {
      // Para outros links, navegar normalmente
      window.location.href = buttonLink;
    }
  };

  // Manipular envio de formulário
  const handleFormSubmit = async (formData, sectionType) => {
    try {
      // Registrar envio do formulário
      await addDoc(collection(db, 'formSubmissions'), {
        formData: formData,
        sectionType: sectionType,
        landingPageId: landingPageData?.id || landingPageId,
        userId: landingPageId,
        timestamp: Timestamp.now(),
        userAgent: navigator.userAgent,
        device: viewMode,
        sessionId: generateSessionId()
      });

      // Registrar como clique também
      await recordButtonClick('Formulário Enviado', '#form-submit', sectionType);

      console.log('Formulário enviado com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      return false;
    }
  };

  // ATUALIZADA: Função para gerar gradiente CSS com melhor suporte
  const generateGradientCSS = (colors, direction = '45deg') => {
    if (!colors || colors.length < 2) return colors?.[0] || '#ffffff';
    
    // Garantir que as cores são válidas
    const validColors = colors.filter(color => color && typeof color === 'string');
    if (validColors.length < 2) return validColors[0] || '#ffffff';
    
    return `linear-gradient(${direction}, ${validColors.join(', ')})`;
  };

  // Estados de loading e erro
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Carregando página...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <div className="error-icon">⚠️</div>
        <h2>Ops! Algo deu errado</h2>
        <p>{error}</p>
        <button 
          className="retry-btn"
          onClick={() => window.location.reload()}
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!landingPageData) {
    return (
      <div className="app-error">
        <div className="error-icon">📄</div>
        <h2>Página não encontrada</h2>
        <p>Nenhuma landing page está disponível no momento.</p>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Meta tags dinâmicas */}
      <title>{landingPageData.seo?.title || landingPageData.title || 'Landing Page'}</title>
      <meta 
        name="description" 
        content={landingPageData.seo?.description || 'Página criada com nosso sistema'} 
      />
      <meta 
        name="keywords" 
        content={landingPageData.seo?.keywords || ''} 
      />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* ATUALIZADO: Estilos globais dinâmicos com melhor suporte */}
      <style>
        {`
          :root {
            --primary-color: ${landingPageData.globalStyles?.primaryColor || '#6e48aa'};
            --secondary-color: ${landingPageData.globalStyles?.secondaryColor || '#9d50bb'};
            --accent-color: ${landingPageData.globalStyles?.accentColor || '#4776e6'};
            --background-color: ${landingPageData.globalStyles?.backgroundColor || '#ffffff'};
            --text-color: ${landingPageData.globalStyles?.textColor || '#333333'};
            --font-family: ${landingPageData.globalStyles?.fontFamily || 'Inter, sans-serif'};
          }
          
          body {
            font-family: var(--font-family);
            color: var(--text-color);
            background-color: var(--background-color);
            margin: 0;
            padding: 0;
            line-height: 1.6;
          }

          /* Estilos para gradientes */
          .gradient-bg {
            background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
          }

          .gradient-text {
            background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          /* NOVOS: Estilos para logo no hero */
          .hero-logo {
            display: block;
            margin-bottom: 20px;
            transition: all 0.3s ease;
          }

          .hero-logo.position-left {
            margin-left: 0;
            margin-right: auto;
          }

          .hero-logo.position-center {
            margin-left: auto;
            margin-right: auto;
          }

          .hero-logo.position-right {
            margin-left: auto;
            margin-right: 0;
          }

          .hero-logo:hover {
            transform: scale(1.05);
          }

          /* NOVOS: Estilos melhorados para imagens customizáveis */
          .custom-image-container {
            position: relative;
            overflow: hidden;
            transition: all 0.3s ease;
          }

          .custom-image-container:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          }

          .custom-image {
            width: 100%;
            height: auto;
            object-fit: cover;
            transition: all 0.3s ease;
          }

          .custom-image:hover {
            transform: scale(1.02);
          }

          .custom-image-placeholder {
            background: #f8f9fa;
            border: 2px dashed #dee2e6;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6c757d;
            font-size: 0.9rem;
            text-align: center;
            transition: all 0.3s ease;
          }

          .custom-image-placeholder:hover {
            background: #e9ecef;
            border-color: #adb5bd;
          }

          /* Melhor suporte para elementos responsivos */
          @media (max-width: 768px) {
            .hero-logo {
              max-width: 80%;
              margin-left: auto;
              margin-right: auto;
            }

            .custom-image-container {
              margin: 8px 0;
            }

            .custom-image {
              border-radius: 8px;
            }
          }
        `}
      </style>

      {/* Componente da Landing Page */}
      <LandingPage 
        data={landingPageData}
        onButtonClick={handleButtonClick}
        onFormSubmit={handleFormSubmit}
        globalStyles={landingPageData.globalStyles}
        viewMode={viewMode}
        generateGradientCSS={generateGradientCSS}
      />

      {/* Analytics Script (Google Analytics, se configurado) */}
      {landingPageData.analytics?.googleAnalyticsId && (
        <>
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${landingPageData.analytics.googleAnalyticsId}`}
          />
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${landingPageData.analytics.googleAnalyticsId}');
            `}
          </script>
        </>
      )}
    </div>
  );
}

export default App;